/**
 * Meta Ads / UTM attribution: capture from URL and persist in cookie for conversion linking.
 */

const COOKIE_NAME = 'controlapag_attribution';
const COOKIE_MAX_AGE_DAYS = 30;

export interface AttributionData {
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmContent?: string;
  utmTerm?: string;
  fbclid?: string;
  fbc?: string;
  fbp?: string;
  /** Primeira página aberta no site (first-touch), sem query. */
  landingPage?: string;
  /**
   * Host de onde veio a primeira visita. Ausente = sem sinal (não é direto):
   * navegador dentro de app de conversa costuma chegar sem referrer.
   */
  referrerHost?: string;
  /**
   * Navegador embutido de rede social na primeira visita. Instagram, Facebook
   * e LinkedIn abrem links sem referrer; o user agent é o único sinal.
   */
  inAppBrowser?: string;
}

/** Ordem importa: o Instagram também carrega `FBAN`/`FBAV` no user agent. */
const IN_APP_BROWSERS: [RegExp, string][] = [
  [/Instagram/i, 'instagram'],
  [/FBAN|FBAV|FB_IAB|FBIOS|FB4A/i, 'facebook'],
  [/LinkedInApp/i, 'linkedin'],
  [/musical_ly|BytedanceWebview|TikTok/i, 'tiktok'],
  [/Threads/i, 'threads'],
  [/Twitter/i, 'twitter'],
  [/Pinterest/i, 'pinterest'],
  [/Snapchat/i, 'snapchat'],
  [/WhatsApp/i, 'whatsapp'],
  [/Telegram/i, 'telegram'],
];

/** Rede social cujo navegador embutido abriu a página, ou `undefined`. */
export function detectInAppBrowser(userAgent: string | undefined): string | undefined {
  if (!userAgent) return undefined;
  return IN_APP_BROWSERS.find(([pattern]) => pattern.test(userAgent))?.[1];
}

function getQueryParam(url: URL, name: string): string | null {
  return url.searchParams.get(name) || null;
}

/**
 * Build fbc from fbclid (Meta format: fb.1.{timestamp}.{fbclid}).
 */
function buildFbc(fbclid: string): string {
  return `fb.1.${Date.now()}.${fbclid}`;
}

/**
 * Parse attribution from current URL (web) or from a given search string.
 */
export function getAttributionFromUrl(search?: string): AttributionData | null {
  if (typeof window === 'undefined') return null;
  const searchString = search ?? window.location.search;
  if (!searchString) return null;
  const url = new URL(window.location.origin + window.location.pathname + searchString);
  const utmSource = getQueryParam(url, 'utm_source');
  const utmMedium = getQueryParam(url, 'utm_medium');
  const utmCampaign = getQueryParam(url, 'utm_campaign');
  const utmContent = getQueryParam(url, 'utm_content');
  const utmTerm = getQueryParam(url, 'utm_term');
  const fbclid = getQueryParam(url, 'fbclid');
  if (!utmSource && !utmMedium && !utmCampaign && !utmContent && !utmTerm && !fbclid) {
    return null;
  }
  const data: AttributionData = {};
  if (utmSource) data.utmSource = utmSource;
  if (utmMedium) data.utmMedium = utmMedium;
  if (utmCampaign) data.utmCampaign = utmCampaign;
  if (utmContent) data.utmContent = utmContent;
  if (utmTerm) data.utmTerm = utmTerm;
  if (fbclid) {
    data.fbclid = fbclid;
    data.fbc = buildFbc(fbclid);
  }
  return data;
}

/**
 * Read attribution from cookie (set by setAttributionCookie).
 */
export function getAttributionFromCookie(): AttributionData | null {
  if (typeof document === 'undefined') return null;
  const raw = document.cookie
    .split(';')
    .map(c => c.trim())
    .find(c => c.startsWith(COOKIE_NAME + '='));
  if (!raw) return null;
  try {
    const value = raw.slice(COOKIE_NAME.length + 1);
    const decoded = decodeURIComponent(value);
    const parsed = JSON.parse(decoded) as AttributionData;
    return Object.keys(parsed).length ? parsed : null;
  } catch {
    return null;
  }
}

/** Campos que dizem DE ONDE a pessoa veio. Gravados uma vez, como grupo. */
const SOURCE_KEYS = [
  'utmSource',
  'utmMedium',
  'utmCampaign',
  'utmContent',
  'utmTerm',
  'fbclid',
  'fbc',
  'referrerHost',
  'inAppBrowser',
] as const satisfies readonly (keyof AttributionData)[];

function hasSourceSignal(data: AttributionData | null | undefined): boolean {
  return !!data && SOURCE_KEYS.some(key => !!data[key]);
}

/**
 * Junta o que já está gravado com o que chegou agora, preservando o primeiro
 * toque: se o cookie já tem origem (UTM, fbclid, host), a origem nova é
 * ignorada inteira (não mistura UTM de uma visita com host de outra). A
 * página de entrada também não muda. Só `fbp` (cookie do Pixel) é atualizado.
 */
export function mergeAttribution(
  existing: AttributionData | null,
  incoming: AttributionData,
): AttributionData {
  const merged: AttributionData = { ...existing };
  if (!hasSourceSignal(existing)) {
    for (const key of SOURCE_KEYS) {
      const value = incoming[key];
      if (value) merged[key] = value;
    }
  }
  if (!merged.landingPage && incoming.landingPage) merged.landingPage = incoming.landingPage;
  if (incoming.fbp) merged.fbp = incoming.fbp;
  return merged;
}

/**
 * Persiste a atribuição no cookie (só via JS; nada de Set-Cookie no servidor,
 * para não quebrar o cache das páginas públicas). Primeiro toque preservado
 * por `mergeAttribution`.
 */
export function setAttributionCookie(data: AttributionData): void {
  if (typeof document === 'undefined') return;
  const existing = getAttributionFromCookie();
  const merged = mergeAttribution(existing, data);
  if (existing && JSON.stringify(existing) === JSON.stringify(merged)) return;
  if (!Object.keys(merged).length) return;
  const value = encodeURIComponent(JSON.stringify(merged));
  const maxAge = COOKIE_MAX_AGE_DAYS * 24 * 60 * 60;
  document.cookie = `${COOKIE_NAME}=${value}; path=/; max-age=${maxAge}; SameSite=Lax`;
}

/**
 * Capture from URL and persist if we have attribution params. Call on landing and register page (web).
 */
export function captureAttributionFromUrl(): void {
  const fromUrl = getAttributionFromUrl();
  if (!fromUrl) return;
  setAttributionCookie(fromUrl);
}

/**
 * First-touch da visita: qual página abriu primeiro, de onde veio (host do
 * referrer) e a UTM/fbclid da URL. Roda em toda rota pelo `_layout.tsx`, então
 * quem chega num guia com `?utm_source=chatgpt.com` não perde a origem.
 * Nada do que já está gravado é sobrescrito (ver `mergeAttribution`).
 */
export function captureLandingPage(): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  let referrerHost: string | undefined;
  try {
    const host = document.referrer ? new URL(document.referrer).host : '';
    if (host && host !== window.location.host) referrerHost = host.slice(0, 120);
  } catch {
    referrerHost = undefined;
  }

  let fromUrl: AttributionData | null = null;
  try {
    fromUrl = getAttributionFromUrl();
  } catch {
    fromUrl = null;
  }

  const inAppBrowser =
    typeof navigator === 'undefined' ? undefined : detectInAppBrowser(navigator.userAgent);

  setAttributionCookie({
    ...fromUrl,
    landingPage: window.location.pathname.slice(0, 300),
    ...(referrerHost && { referrerHost }),
    ...(inAppBrowser && { inAppBrowser }),
  });
}

function getMetaFbpFromCookie(): string | null {
  if (typeof document === 'undefined') return null;
  const raw = document.cookie
    .split(';')
    .map(c => c.trim())
    .find(c => c.startsWith('_fbp='));
  return raw ? raw.slice('_fbp='.length).trim() : null;
}

/**
 * Return attribution to send with register payload (provider). Includes fbp from Meta Pixel cookie if present.
 */
export function getAttributionForSubmit(): Partial<{
  utmSource: string;
  utmMedium: string;
  utmCampaign: string;
  utmContent: string;
  utmTerm: string;
  fbclid: string;
  fbc: string;
  fbp: string;
  landingPage: string;
  referrerHost: string;
  inAppBrowser: string;
}> {
  const fromCookie = getAttributionFromCookie();
  const fbp = fromCookie?.fbp ?? getMetaFbpFromCookie();
  const base = fromCookie
    ? {
        ...(fromCookie.utmSource && { utmSource: fromCookie.utmSource }),
        ...(fromCookie.utmMedium && { utmMedium: fromCookie.utmMedium }),
        ...(fromCookie.utmCampaign && { utmCampaign: fromCookie.utmCampaign }),
        ...(fromCookie.utmContent && { utmContent: fromCookie.utmContent }),
        ...(fromCookie.utmTerm && { utmTerm: fromCookie.utmTerm }),
        ...(fromCookie.fbclid && { fbclid: fromCookie.fbclid }),
        ...(fromCookie.fbc && { fbc: fromCookie.fbc }),
        ...(fromCookie.landingPage && { landingPage: fromCookie.landingPage }),
        ...(fromCookie.referrerHost && { referrerHost: fromCookie.referrerHost }),
        ...(fromCookie.inAppBrowser && { inAppBrowser: fromCookie.inAppBrowser }),
      }
    : {};
  return {
    ...base,
    ...(fbp && { fbp }),
  };
}
